import _, {isBoolean, isFunction, isObject, map} from 'lodash';
import axios from '../../v2/app/services/axiosService';
import presentationFileService from './presentationFileService';
import DOMPurify from 'dompurify';
import moment from 'moment-timezone';

const LANDSCAPE = 'landscape';
const PORTRAIT = 'portrait';
const BOTH = 'both';

const service = {
  async doesPathExist(presentationID, path) {
    try {
      const options = {
        params: {
          path,
        },
      }
      const response = await axios.get(`/api/presentations/${presentationID}/files/check-path`, options);
      return response.data.exists;
    } catch (error) {
      return false;
    }
  },
  getMaxDate() { // MLD-7079
    const now = new Date();
    const year = now.getFullYear() + 5;
    return `${year}-12-31`;
  },
  getMaxDateLocal() { // MLD-7079
    const now = new Date();
    const year = now.getFullYear() + 5;
    return `${year}-12-31T00:00`;
  },
  getIcon(type, defaultIcon = 'fal fa-fw fa-file-alt') {
    const icon = _.find(this.getIcons(), i => i.name === type);
    return icon ? icon.value : defaultIcon;
  },
  getIcons() { // MLD-2156
    return [
      {name: 'Audio', value: 'fal fa-fw fa-file-music'},
      {name: 'HTML', value: 'fal fa-fw fa-browser'},
      {name: 'Image', value: 'fal fa-fw fa-file-image'},
      {name: 'PDF', value: 'fal fa-fw fa-file-pdf'},
      {name: 'PowerPoint', value: 'fal fa-fw fa-file-powerpoint'},
      {name: 'MS Word', value: 'fal fa-fw fa-file-word'},
      {name: 'MS Excel', value: 'fal fa-fw fa-file-excel'},
      {name: 'ODT', value: 'fal fa-fw fa-file'},
      {name: 'ODP', value: 'fal fa-fw fa-file'},
      {name: 'ODS', value: 'fal fa-fw fa-file-spreadsheet'},
      {name: 'Text', value: 'fal fa-fw fa-file-alt'},
      {name: 'Video', value: 'fal fa-fw fa-film-alt'},
    ];
  },
  getPdfViewModes() { // MLI-24
    // @see https://www.pdftron.com/documentation/ios/guides/viewer/view-mode/layout/
    return [
      {id: 'e_trn_single_page', name: 'Single Page', description: 'Displays one page at a time, swiping left and right to change pages.'},
      {id: 'e_trn_single_continuous', name: 'Single Page with Vertical Scrolling', description: 'Displays one page at a time, scrolling up and down to change pages.'},
      {id: 'e_trn_facing', name: 'Facing', description: 'Displays two pages at a time, with odd-numbered pages on left, swiping left and right to change pages.'},
      {id: 'e_trn_facing_continuous', name: 'Facing with Vertical Scrolling', description: 'Displays pages in two columns, with odd-numbered pages on left, scrolling up and down to change pages.'},
      {id: 'e_trn_facing_cover', name: 'Cover Facing', description: 'Displays two pages at a time, with odd-numbered pages on right, swiping left and right to change pages.'},
      {id: 'e_trn_facing_continuous_cover', name: 'Cover Facing with Vertical Scrolling', description: 'Display pages in two columns, with odd-numbered pages on right, scrolling up and down to change pages.'},
    ];
  },
  getPdfPageViewModes() { // MLD-3936
    // @see https://www.pdftron.com/documentation/ios/guides/viewer/view-mode/fit/
    return [
      {
        id: 'e_trn_fit_page',
        name: 'Fit page',
        description: 'The zoom is set so the entire page is visible without scrolling.',
      },
      {
        id: 'e_trn_fit_width',
        name: 'Fit width',
        description: 'The zoom is set so that the page\'s width matches the viewer\'s width.',
      },
      {
        id: 'e_trn_fit_height',
        name: 'Fit height',
        description: 'The zoom is set so that the page\'s height matches the viewer\'s height.',
      },
      {
        id: 'e_trn_zoom',
        name: 'Zoom',
        description: 'The viewer\'s zoom is not adjusted based on the page.',
      },
    ]
  },
  convertsToPDF(type) { // MLI-24
    const fileType = _.find(this.getTypes(), t => t.id === type);
    if (fileType) {
      return fileType.pdf;
    }
    return false;
  },
  resetFilter() {
    return {
      name: null,
      status: null,
      type: null,
      group: null,
      label: null,
      order_by: null,
      order_dir: null,
    };
  },
  pdfTypes() {
    return this.getTypes().filter(t => t.pdf).map(t => t.id);
  },
  getTypes(withEmpty = '') {
    let types = [
      {id: 'PDF', name: 'PDF', builder: true, pdf: true,},
      {id: 'HTML', name: 'HTML', builder: false, pdf: false,},
      {id: 'PowerPoint', name: 'PowerPoint', builder: true, pdf: true,},
      {id: 'Image', name: 'Image', builder: false, pdf: false,}, // MLD-783
      {id: 'Audio', name: 'Audio', builder: false, pdf: false,},
      {id: 'Video', name: 'Video', builder: false, pdf: false,},
      {id: 'URL', name: 'External Website or URL', builder: false, pdf: false,}, // MLD-1194
      {id: 'MS Word', name: 'MS Word', builder: true, pdf: true,},
      {id: 'MS Excel', name: 'MS Excel', builder: false, pdf: true,},
      //{id: 'ePub', name: 'ePub', builder: false, pdf: true,},
      //{id: 'Text', name: 'Text', builder: false, pdf: true,},
      //{id: 'Rich Text', name: 'Rich Text', builder: false, pdf: true,},
      //{id: 'ODT', name: 'OpenOffice Document', builder: true, pdf: true,}, // MLD-834
      //{id: 'ODP', name: 'OpenOffice Presentation', builder: true, pdf: true,}, // MLD-834
      //{id: 'ODS', name: 'OpenOffice Spreadsheet', builder: true, pdf: true,}, // MLD-834
      {id: 'DWG', name: 'AutoCAD Drawing', builder: true, pdf: true,}, // MLD-185
      {id: 'DXF', name: 'AutoCAD Interchange Format', builder: true, pdf: true,}, // MLD-185
      {id: 'ZIP File', name: 'ZIP File', builder: false, pdf: false,}, // MLD-5606
    ];
    if (withEmpty) {
      types.unshift({id: '', name: withEmpty});
    }
    return types;
  },
  getAcceptableFileTypes(presentationType) {
    switch (presentationType) {
      case 'HTML':
      case 'ZIP File': // MLD-5606
        // MLD-3326 - alternative mime types so it will work on Windows
        return 'application/zip,application/x-zip,application/zip-compressed,application/x-zip-compressed';
      case 'PDF':
        return 'application/pdf';
      case 'MS Word':
        return '.doc,.docx';
      case 'MS Excel': // MLD-2674
        return '.xls,.xlsx';
      case 'PowerPoint':
        return '.ppt,.pptx,.pptm'; // MLD-6004
      case 'Video':
        return '.mp4,.mov,.mpg';
      case 'ePub':
        return '.epub';
      case 'Image': // MLD-783, MLD-3103 added TIFF
        return '.jpg,.jpeg,.png,.gif,.tif,.tiff,.svg';
      case 'ODT': // MLD-834
        return '.odt';
      case 'ODP': // MLD-834
        return '.odp';
      case 'ODS': // MLD-834
        return '.ods';
      case 'DWG': // MLD-185
        return '.dwg';
      case 'DXF': // MLD-185
        return '.dxf';
      case 'Illustrator': // MLD-5165
        return '.ai';
    }
    return null;
  },
  async getSignedURL(presentationID) {
    const response = await axios.get(`/api/presentations/${presentationID}/signed`);
    return response.data;
  },
  async getJobs(presentationID) {
    const response = await axios.get(`/api/presentations/${presentationID}/jobs`);
    return response.data;
  },
  async getPresentations(teamID, form) { // MLD-1809
    const response = await axios.post(`/api/teams/${teamID}/presentations`, form);
    if (response.data.meta) {
      let presentations = response.data.data;
      presentations.forEach(p => {
        p.folders = p.folders.data;
        p.groups = p.groups.data;
        p.labels = p.labels.data;
      })
      return {
        data: presentations,
        meta: response.data.meta,
      };
    } else {
      return response.data;
    }
  },
  async getPresentationsForCurrentUser() {
    const response = await axios.get(`/api/user/presentations`);
    return response.data;
  },
  async getPresentationsForUser(userID) { // MLD-5194
    const response = await axios.get(`/api/users/${userID}/presentations`);
    return response.data;
  },
  async getPresentation(presentationId) {
    try {
      const response = await axios.get(`/api/admin/presentations/${presentationId}`);

      let presentation = response.data;

      presentation.form_names = map(presentation.forms, f => {
        return {name: f.name, id: f.id};
      });

      this.decorateFolders(presentation);
      this.setFileEditability(presentation);
      return response.data;
    } catch (err) {
      console.log(`Error retrieving presentation ${DOMPurify.sanitize(presentationId)}`, err.response.data);
      throw err;
    }
  },
  populateForm(form, presentation, user) {
    let timezone = user.timezone;
    if (!timezone) {
      timezone = 'UTC';
    }
    form.name = presentation.name;
    form.alias = presentation.alias; // MLD-2650
    form.external_url = presentation.external_url; // MLD-1194
    form.external_id = presentation.external_id; // MLD-994
    form.access_code = presentation.access_code; // MLD-1556
    form.main_path = presentation.main_path;
    form.thumbnail_file_id = (presentation.thumbnail) ? presentation.thumbnail.id : null;
    form.owner_id = presentation.owner_id ? presentation.owner_id : ''; // MLD-1970
    if (form.owner_id) { // MLD-1970
      if (user.id === form.owner_id) {
        form.userPresentation = 1;
      } else {
        form.userPresentation = 2;
      }
    }
    form.status = presentation.status;
    form.description = presentation.description;
    form.presentation_type = presentation.presentation_type;
    form.groups = presentation.groups;
    form.labels = presentation.labels;
    form.brand_id = presentation.brand_id ?? ''; // MLD-4796
    form.products = (presentation.products) ? presentation.products : []; // MLD-4360
    form.folder_ids = (presentation.folders) ? map(presentation.folders, f => f.id) : [];
    form.release_at = (presentation.release_at) ? moment.utc(presentation.release_at).tz(timezone).format('YYYY-MM-DDTHH:mm') : null;
    form.expires_at = (presentation.expires_at) ? moment.utc(presentation.expires_at).tz(timezone).format('YYYY-MM-DDTHH:mm') : null;
    form.new_until_at = (presentation.new_until_at) ? moment.utc(presentation.new_until_at).tz(timezone).format('YYYY-MM-DD') : null; // MLD-5639
    form.reviewer_id = presentation.reviewer_id ? presentation.reviewer_id : ''; // MLD-1838
    form.content_updated_at = (presentation.content_updated_at) ? moment.utc(presentation.content_updated_at).tz(timezone).format('YYYY-MM-DDTHH:mm') : null; // MLD-2245
    form.send_activity_to_crm = presentation.send_activity_to_crm; // MLD-4729
    form.settings.ga_profile_id = presentation.settings.ga_profile_id;
    form.settings.rotation_mode = presentation.settings.rotation_mode;
    form.settings.can_be_shared = presentation.settings.can_be_shared;
    form.settings.can_be_shared_link = presentation.settings.can_be_shared_link; // MLD-1641
    form.settings.can_be_shared_space = isBoolean(presentation.settings.can_be_shared_space) ? presentation.settings.can_be_shared_space : false; // MLD-4908
    form.settings.can_be_shared_text = isBoolean(presentation.settings.can_be_shared_text) ? presentation.settings.can_be_shared_text : false; // MLD-7490
    form.settings.are_shared_links_secure = isBoolean(presentation.settings.are_shared_links_secure) ? presentation.settings.are_shared_links_secure : false; // MLD-5455
    form.settings.can_be_faxed = !!presentation.settings.can_be_faxed; // MLD-3298
    form.settings.can_be_presented_by_user = presentation.settings.can_be_presented_by_user; // MLD-2254
    form.settings.can_be_forwarded = presentation.settings.can_be_forwarded; // MLD-1421
    form.settings.can_be_downloaded = presentation.settings.can_be_downloaded;
    form.settings.can_be_annotated = presentation.settings.can_be_annotated;
    form.settings.can_be_exported = presentation.settings.can_be_exported; // MLD-2038
    form.settings.can_be_handout = presentation.settings.can_be_handout ?? false; // MLD-6661
    form.can_be_used_in_haystack = presentation.can_be_used_in_haystack; // MLD-7374
    form.settings.can_be_printed = presentation.settings.can_be_printed; // MLD-2038
    form.settings.can_be_printed_by_user = presentation.settings.can_be_printed_by_user; // MLD-2267
    form.settings.can_user_use_cdn = presentation.settings.can_user_use_cdn; // MLD-2254
    form.settings.can_share_cdn_link = presentation.settings.can_share_cdn_link; // MLD-2254
    form.settings.can_be_used_in_builder = presentation.settings.can_be_used_in_builder; // MLD-2932
    form.settings.always_show_toolbar = presentation.settings.always_show_toolbar; // MLD-976
    form.settings.show_exit_instructions = isBoolean(presentation.settings.show_exit_instructions) ? presentation.settings.show_exit_instructions : true; // MLD-4721
    form.settings.show_search_button = presentation.settings.show_search_button; // MLD-1594
    form.settings.show_settings_button = presentation.settings.show_settings_button; // MLD-1594
    form.settings.show_navigation_lists_button = presentation.settings.show_navigation_lists_button; // MLD-1594
    form.settings.show_thumbnail_browser_button = presentation.settings.show_thumbnail_browser_button; // MLD-1594
    form.settings.pdf_view_mode = presentation.settings.pdf_view_mode ? presentation.settings.pdf_view_mode : ''; // MLI-24
    form.settings.pdf_page_view_mode = presentation.settings.pdf_page_view_mode ? presentation.settings.pdf_page_view_mode : ''; // MLD-3936
    form.settings.pdf_disable_widget_highlighting = isBoolean(presentation.settings.pdf_disable_widget_highlighting) ? presentation.settings.pdf_disable_widget_highlighting : false;
    form.settings.pdf_use_full_api = isBoolean(presentation.settings.pdf_use_full_api) ? presentation.settings.pdf_use_full_api : true; // MLD-7991
    form.settings.pdf_use_webviewer_server = isBoolean(presentation.settings.pdf_use_webviewer_server) ? presentation.settings.pdf_use_webviewer_server : false; // MLD-7991
    form.settings.is_document_slider_enabled = isBoolean(presentation.settings.is_document_slider_enabled) ? presentation.settings.is_document_slider_enabled : true; // MLD-3937
    form.settings.is_navigation_history_enabled = isBoolean(presentation.settings.is_navigation_history_enabled) ? presentation.settings.is_navigation_history_enabled : true; // MLD-3937
    form.is_protected = presentation.is_protected; // MLD-819
    form.is_rateable = presentation.is_rateable; // MLD-875
    form.show_in_main_list = presentation.show_in_main_list; // MLD-928
    form.is_featured = presentation.is_featured; // MLD-5574
    form.share_theme_id = presentation.share_theme_id ? presentation.share_theme_id : ''; // MLD-967
    form.share_template_id = presentation.share_template_id ? presentation.share_template_id : ''; // MLD-1773
    form.is_template_required = presentation.is_template_required; // MLD-3997
    form.locale = presentation.locale; // MLD-1323
    form.custom_javascript = presentation.custom_javascript; // MLD-1652
    form.facebook_pixel = presentation.facebook_pixel; // MLD-1652
    form.google_analytics_id = presentation.google_analytics_id; // MLD-1652
    form.hubspot_pixel = presentation.hubspot_pixel; // MLD-1804
    form.linkedin_pixel = presentation.linkedin_pixel; // MLD-1652
    form.slack_url = presentation.slack_url; // MLD-1708
    form.is_internal_use_only = presentation.is_internal_use_only; // MLD-2864
    form.is_pdf_form = presentation.is_pdf_form; // MLD-1797
    form.notifications = _.cloneDeep(presentation.notifications); // MLD-1807
    form.subdomain = presentation.subdomain; // MLD-1829

    // MLD-2625
    if (presentation.vaultConfiguration) {
      form.vaultConfiguration.synchronize_expiration_date = presentation.vaultConfiguration.synchronize_expiration_date;
      form.vaultConfiguration.synchronize_title = presentation.vaultConfiguration.synchronize_title;
    }

    // MLD-4240
    form.is_posthog_enabled = presentation.is_posthog_enabled;
    form.posthog_api_key = presentation.posthog_api_key;
    form.posthog_project_id = presentation.posthog_project_id;
  },
  presentationFormFields() {
    return {
      file: null,
      external_url: null, // MLD-1194
      file_url: null,
      file_url_info: null,
      name: '',
      alias: null, // MLD-2650
      external_id: null, // MLD-994
      access_code: null, // MLD-1556
      main_path: null,
      thumbnail_file_id: null,
      status: 'Draft',
      locale: 'en', // MLD-1323
      description: '',
      presentation_type: '',
      groups: [],
      labels: [],
      folder_id: '',
      folder_ids: [],
      brand_id: '', // MLD-4796
      products: [], // MLD-4360
      release_at: null,
      expires_at: null,
      new_until_at: null, // MLD-5639
      reviewer_id: '', // MLD-1838
      send_activity_to_crm: true, // MLD-4729
      is_featured: false, // MLD-5574
      settings: {
        ga_profile_id: null,
        hide_status_bar: false,
        rotation_mode: BOTH,
        menu_bar_taps: 2,
        bounce_enabled: true,
        bounce_zoom_enabled: true,
        clear_cache_on_open: false,
        clear_cache_on_exit: false,
        can_be_shared: false,
        can_be_shared_link: false, // MLD-1641
        are_shared_links_secure: false, // MLD-5455
        can_be_faxed: false, // MLD-3298
        can_be_presented_by_user: false, // MLD-2254
        can_be_forwarded: false, // MLD-1421
        can_be_downloaded: false,
        can_be_annotated: false,
        can_be_exported: false, // MLD-2038
        can_be_handout: false, // MLD-6661
        can_be_printed: false, // MLD-2038
        can_be_printed_by_user: false, // MLD-2267
        can_user_use_cdn: false, // MLD-2254
        can_be_used_in_builder: false, // MLD-2932
        can_share_cdn_link: false, // MLD-2254
        always_show_toolbar: false, // MLD-976
        show_search_button: false, // MLD-1594
        show_exit_instructions: true, // MLD-4721
        show_settings_button: false, // MLD-1594
        show_navigation_lists_button: false, // MLD-1594
        show_thumbnail_browser_button: false, // MLD-1594
        pdf_view_mode: '', // MLI-24
        pdf_page_view_mode: '', // MLD-3936
        pdf_use_full_api: true, // MLD-7991
        pdf_use_webviewer_server: false, // MLD-7991
        is_document_slider_enabled: true, // MLD-3937
        is_navigation_history_enabled: true, // MLD-3937
      },
      can_be_used_in_haystack: true, // MLD-7374
      userPresentation: 0,
      owner_id: '',
      is_slide_deck: false, // MLD-6435
      is_custom: false, // MLD-3281
      is_protected: false, // MLD-819
      is_rateable: true, // MLD-875
      show_in_main_list: true, // MLD-928
      share_theme_id: '', // MLD-967
      share_template_id: '', // MLD-1773
      is_template_required: false, // MLD-3997
      custom_javascript: null, // MLD-1652
      facebook_pixel: null, // MLD-1652
      google_analytics_id: null, // MLD-1652
      hubspot_pixel: null, // MLD-1804
      linkedin_pixel: null, // MLD-1652
      slack_url: null, // MLD-1708
      is_internal_use_only: false, // MLD-2864
      is_pdf_form: false, // MLD-1797
      notifications: {
        respondent: {
          enabled: false,
          recipients: null,
        },
        self: {
          enabled: false,
          recipients: null,
        }
      }, // MLD-1807
      subdomain: null, // MLD-1829
      content_updated_at: null, // MLD-2245
      vaultConfiguration: { // MLD-2625
        synchronize_expiration_date: true,
        synchronize_title: true,
      },
      // MLD-4240
      is_posthog_enabled: false,
      posthog_api_key: null,
      posthog_project_id: null,
    };
  },
  configureDefaults(team, form) {
    if (form.settings) {
      form.settings.can_be_presented_by_user = team.default_user_present_browser; // MLD-2254
      form.settings.can_be_shared = team.default_share_email ?? false;
      form.settings.can_be_shared_link = team.default_share_link ?? false;
      form.settings.can_be_shared_space = !!team.default_share_space; // MLD-4908
      form.settings.can_be_shared_text = !!team.default_share_text; // MLD-7490
      form.settings.can_be_faxed = !!team.default_fax; // MLD-3298
      form.settings.can_be_annotated = team.default_annotate;
      form.settings.can_be_exported = team.default_export_file;
      form.settings.can_be_handout = team.default_can_be_handout; // MLD-6661
      form.settings.can_be_printed_by_user = team.default_user_print_file; // MLD-2267
      form.can_be_used_in_haystack = team.default_use_in_haystack; // MLD-7374

      form.settings.can_be_downloaded = team.default_download_file;
      form.settings.can_be_printed = team.default_print_file;
      form.settings.can_be_forwarded = team.default_forward_file;
      form.is_rateable = team.default_rate_file;
      form.send_activity_to_crm = team.default_send_activity_to_crm; // MLD-4729

      // MLD-6491
      form.settings.always_show_toolbar = team.default_ios_always_show_toolbar;
      form.settings.show_search_button = team.default_ios_show_search_button;
      form.settings.show_settings_button = team.default_ios_show_settings_button;
      form.settings.show_navigation_lists_button = team.default_ios_show_navigation_lists_button;
      form.settings.show_thumbnail_browser_button = team.default_ios_show_thumbnail_browser_button;
      form.settings.is_document_slider_enabled = team.default_ios_is_document_slider_enabled;
      form.settings.is_navigation_history_enabled = team.default_ios_is_navigation_history_enabled;
      form.settings.show_exit_instructions = team.default_ios_show_exit_instructions;

    } else {
      form.can_be_presented_by_user = team.default_user_present_browser; // MLD-2254
      form.can_be_shared = team.default_share_email;
      form.can_be_shared_link = team.default_share_link;
      form.can_be_shared_space = team.default_share_space;
      form.can_be_shared_text = team.default_share_text; // MLD-7490
      form.settings.can_be_faxed = !!team.default_fax; // MLD-3298
      form.can_be_annotated = team.default_annotate;
      form.can_be_handout = team.default_can_be_handout; // MLD-6661
      form.can_be_exported = team.default_export_file;
      form.can_be_printed_by_user = team.default_user_print_file; // MLD-2267
      form.can_be_downloaded = team.default_download_file;
      form.can_be_printed = team.default_print_file;
      form.can_be_forwarded = team.default_forward_file;
      form.is_rateable = team.default_rate_file;
      form.send_activity_to_crm = team.default_send_activity_to_crm; // MLD-4729
      form.can_be_used_in_haystack = team.default_use_in_haystack; // MLD-7374

      // MLD-6491
      form.always_show_toolbar = team.default_ios_always_show_toolbar;
      form.show_search_button = team.default_ios_show_search_button;
      form.show_settings_button = team.default_ios_show_settings_button;
      form.show_navigation_lists_button = team.default_ios_show_navigation_lists_button;
      form.show_thumbnail_browser_button = team.default_ios_show_thumbnail_browser_button;
      form.is_document_slider_enabled = team.default_ios_is_document_slider_enabled;
      form.is_navigation_history_enabled = team.default_ios_is_navigation_history_enabled;
      form.show_exit_instructions = team.default_ios_show_exit_instructions;
    }

    form.is_template_required = team.quickshare_template_required; // MLD-3997
  },
  presentationFormData() {
    return {
      menuBarTaps: [
        {name: '2 taps', id: 2},
        {name: '3 taps', id: 3}
      ],
      rotationModes: [
        {name: 'Landscape or Portrait mode', id: BOTH},
        {name: 'Landscape mode only', id: LANDSCAPE},
        {name: 'Portrait mode only', id: PORTRAIT}
      ],
      booleans: [
        {name: 'Yes', id: 1},
        {name: 'No', id: 0}
      ]
    };
  },
  async create(form, progressCallback = null) {
    if (form.busy) {
      return;
    }

    let data = _.omit(form, [
      'groups',
      'labels',
      'errors',
      'success',
      'successful',
      'busy',
      'startProcessing',
      'finishProcessing',
      'resetStatus',
      'setErrors',
      'folder_ids', // MLD-1674
    ]);

    let formData = new FormData();
    _.forOwn(data, (value, key) => {
      if (key === 'settings') {
        // The FormData object cannot have nested keys so it is converted to a JSON-encoded string first.
        formData.append(key, JSON.stringify(value));
      } else if (value) {
        formData.append(key, value);
      }
    });
    _.each(form.groups, g => {
      formData.append('groups[]', g.id);
    });
    _.each(form.labels, l => {
      formData.append('labels[]', l.id);
    });
    form.folder_ids.forEach(folderID => { // MLD-1676
      formData.append('folder_ids[]', folderID);
    });
    form.products.forEach(product => { // MLD-5005
      if (isObject(product)) {
        formData.append('products[]', product.id);
      } else {
        formData.append('products[]', product);
      }
    })
    formData.set('is_rateable', form.is_rateable); // MLD-2148
    formData.set('send_activity_to_crm', form.send_activity_to_crm); // MLD-4729
    formData.set('show_in_main_list', form.show_in_main_list); // MLD-1676
    formData.set('is_featured', form.is_featured); // MLD-5574
    formData.set('is_internal_use_only', form.is_internal_use_only); // MLD-5636

    const endpoint = form.video_url ? '/api/admin/presentations/import-video' : '/api/admin/presentations';
    // MLD-5995
    const config = {}
    if (isFunction((progressCallback))) {
      config.onUploadProgress = progressCallback;
    }
    const response = await axios.post(endpoint, formData, config);
    return response.data;
  },
  async update(presentationID, form) {
    let data = form.fields();
    data.groups = map(form.groups, (g) => {
      return g.id
    });
    data.labels = map(form.labels, (l) => {
      return l.id
    });
    data.products = form.products.map(p => p.id); // MLD-4360

    form.startProcessing();
    try {
      const response = await axios.put(`/api/admin/presentations/${presentationID}`, data);
      form.finishProcessing();
      return response.data;
    } catch (err) {
      form.busy = false;
      form.setErrors(err.response.data);
      throw err;
    }
  },
  decorateFolders(presentation) {
    //console.log('decorateFolders()', presentation.id, presentation.internal_folders);
    let folders = map(presentation.internal_folders, f => {
      let r = {
        parent: null,
        name: null,
        path: '',
        id: null
      };
      if ('/' === f) {
        return r;
      }
      let segments = _.split(f, '/');
      //console.log('folder', f, segments);
      if (segments.length > 1) {
        r.parent = f.slice(0, f.lastIndexOf('/'));
        r.name = DOMPurify.sanitize(f.slice(f.lastIndexOf('/') + 1)); // MLD-4779
        r.path = `${f}`;
        r.value = r.path;
      } else {
        r.parent = null;
        r.name = DOMPurify.sanitize(f); // MLD-4779
        r.path = f;
        r.value = f;
      }
      return r;
    });
    presentation.internal_folders = _.sortBy(folders, 'path');
  },
  setFileEditability(presentation) {
    if (_.isObject(presentation) && _.isArray(presentation.files)) {
      presentation.files.forEach(file => {
        presentationFileService.setEditability(file);
      });
    }
  },
  deletePresentation(presentation) {
    if (presentation.is_protected) {
      return Promise.reject(new Error('Protected presentation cannot be deleted.'));
    }
    return axios.delete(`/api/admin/presentations/${presentation.id}`);
  },
  setThumbnail(presentation, file) {
    if (file.presentation_id !== presentation.id) {
      return new Promise();
    }
    return axios.put(`/api/presentations/${presentation.id}/thumbnail/${file.id}`);
  },
  canBeAnnotated(presentation, value) {
    return axios.request({
      method: value ? 'put' : 'delete',
      url: `/api/admin/presentations/${presentation.id}/settings/can-be-annotated`,
    });
  },
  canBeDownloaded(presentation, value) {
    return axios.request({
      method: value ? 'put' : 'delete',
      url: `/api/admin/presentations/${presentation.id}/settings/can-be-downloaded`,
    });
  },
  canBeRated(presentation, value) {
    return axios.request({
      method: value ? 'put' : 'delete',
      url: `/api/admin/presentations/${presentation.id}/settings/is-rateable`,
    });
  },
  canBeShared(presentation, value) {
    return axios.request({
      method: value ? 'put' : 'delete',
      url: `/api/admin/presentations/${presentation.id}/settings/can-be-shared`,
    });
  },
  canBeSharedLink(presentation, value) {
    return axios.request({
      method: value ? 'put' : 'delete',
      url: `/api/admin/presentations/${presentation.id}/settings/can-be-shared-link`,
    });
  },
  canBeUsedInBuilder(presentation, value) {
    return axios.request({
      method: value ? 'put' : 'delete',
      url: `/api/admin/presentations/${presentation.id}/settings/can-be-used-in-builder`,
    });
  },
  canBeForwarded(presentation, value) {
    return axios.request({
      method: value ? 'put' : 'delete',
      url: `/api/admin/presentations/${presentation.id}/settings/can-be-forwarded`,
    });
  },
  async changeShareTheme(presentationID, shareThemeID) {
    const form = {
      share_theme_id: shareThemeID,
    };
    const response = await axios.put(`/api/presentations/${presentationID}/share-theme`, form);
    return response.data;
  },
  changeStatus(presentationId, status) {
    return axios.put(`/api/presentations/${presentationId}/status`, {status: status});
  },
  async changeOwner(presentationId, ownerID) {
    const form = {
      owner_id: ownerID,
    };
    const response = await axios.put(`/api/presentations/${presentationId}/owner`, form);
    return response.data;
  },
  async bulkDelete(presentationIDs) {
    const form = {
      presentation_ids: presentationIDs,
    };
    const response = await axios.post(`/api/presentations/bulk-delete`, form);
    return response.data;
  },
  async copyPresentation(presentationID, form) { // MLD-930
    const response = await axios.post(`/api/presentations/${presentationID}/copy`, form);
    return response.data;
  },
  async exportPresentations(presentationIDs, includeGroupNames = false, includeFolderNames = false) { // MLD-1355
    const form = {
      presentations: presentationIDs,
      include_groups: includeGroupNames,
      include_folders: includeFolderNames, // MLD-1819
    };
    const response = await axios.post(`/api/presentations/export`, form);
    let data = response.data;
    // https://stackoverflow.com/questions/43432892/force-download-get-request-using-axios
    let blob = new Blob([data], {type: 'text/csv'});
    let link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `presentations.csv`;
    link.dispatchEvent(new MouseEvent(`click`, {bubbles: true, cancelable: true, view: window}));
    return data;
  },
  async getURL(url) { // MLD-1194
    const response = await axios.post(`/api/url/info`, {url});
    return response.data;
  },
  async downloadFile(presentation, file) { // MLD-2673
    const axiosConfig = {
      responseType: 'blob',
      withCredentials: false,
    };
    const {data} = await axios.get(file.url, axiosConfig)
    const blobURL = URL.createObjectURL(new Blob([data]));
    // https://stackoverflow.com/questions/43432892/force-download-get-request-using-axios
    let link = document.createElement('a');
    link.href = blobURL;
    link.download = `${presentation.slug}.${file.extension}`;
    link.dispatchEvent(new MouseEvent(`click`, {bubbles: true, cancelable: true, view: window}));
    return true;
  }
}

export default service;
